import React from "react";
import styled from "styled-components";
import { injectIntl, defineMessages, FormattedMessage } from "react-intl";
import moment from "moment";
import DatePickerGroup from "../DatePickerGroup";
import MultiCheckBoxGroup from "../MultiCheckBoxGroup";
import { RESTRICTION_TYPE, UPDATE_ENTITY_ACTIONS, WEEK_DAYS } from "../../../Common/constants";
import FieldErrorList from "../FieldErrorList";

const messages = defineMessages({
  travelDate: {
    id: "app.periodConditions.travelDate",
    description: "Travel dates",
    defaultMessage: "Travel dates",
  },
  startTravelable: {
    id: "app.periodConditions.startActive",
    description: "Stay active",
    defaultMessage: "Stay active",
  },
  endTravelable: {
    id: "app.periodConditions.endActive",
    description: "End active",
    defaultMessage: "End active",
  },
  weekDays: {
    id: "app.periodConditions.weekDays",
    description: "Week days (optional)",
    defaultMessage: "Weekdays (optional)",
  },
  weekDaysDescription: {
    id: "app.periodConditions.weekDaysDescription",
    description:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
    defaultMessage:
      "This rule will only apply if the time of the reservation occurs on any of the marked days (if all the fields are left empty it means they are enabled every day)",
  },
  monday: {
    id: "app.periodConditions.monday",
    description: "Monday",
    defaultMessage: "Monday",
  },
  tuesday: {
    id: "app.periodConditions.tuesday",
    description: "Tuesday",
    defaultMessage: "Tuesday",
  },
  wednesday: {
    id: "app.periodConditions.wednesday",
    description: "Wednesday",
    defaultMessage: "Wednesday",
  },
  thursday: {
    id: "app.periodConditions.thursday",
    description: "Thursday",
    defaultMessage: "Thursday",
  },
  friday: {
    id: "app.periodConditions.friday",
    description: "Friday",
    defaultMessage: "Friday",
  },
  saturday: {
    id: "app.periodConditions.saturday",
    description: "Saturday",
    defaultMessage: "Saturday",
  },
  sunday: {
    id: "app.periodConditions.sunday",
    description: "Sunday",
    defaultMessage: "Sunday",
  },
  deleteTravelDate: {
    id: "app.periodConditions.deleteTravelDate",
    description: "Remove travel date",
    defaultMessage: "Remove travel date",
  },
  endDateAfterStart: {
    id: "app.periodConditions.endDateAfterStart",
    description: "The end date must be greater than the start date and greater than today",
    defaultMessage: "The end date must be greater than the start date and greater than today",
  },
  properties: {
    id: "app.periodConditions.properties",
    description: "Properties",
    defaultMessage: "Properties",
  },
  propertiesDescription: {
    id: "app.periodConditions.propertiesDescription",
    description: "If you do not select any, the range applies to all properties.",
    defaultMessage: "If you do not select any, the range applies to all properties.",
  },
  search: {
    id: "app.periodConditions.search",
    description: "Search",
    defaultMessage: "Search",
  },
  propertiesAvailable: {
    id: "app.periodConditions.propertiesAvailable",
    description: "Available properties",
    defaultMessage: "Available properties",
  },
  propertiesSelected: {
    id: "app.periodConditions.propertiesSelected",
    description: "Selected properties",
    defaultMessage: "Selected properties",
  },
});

const restrictionTypes = [RESTRICTION_TYPE.OPEN_TRAVEL];

const restrictionTranslations = {
  OPEN_TRAVEL: {
    TITLE: <FormattedMessage {...messages.travelDate} />,
    START: <FormattedMessage {...messages.startTravelable} />,
    END: <FormattedMessage {...messages.endTravelable} />,
  },
};

const Container = styled.div`
  a {
    cursor: pointer;
  }
`;

const DatePickerContainer = styled.div`
  fieldset {
    border-bottom: none;
  }

  .react-datepicker__input-container {
    width: 300px;
  }
`;

const DeleteTravelDateLink = styled.a`
  margin-top: 3px;
  margin-left: 15px;
`;

const DatePickerGroupStyled = styled.div`
  ${props =>
    props && props.restrictionType === RESTRICTION_TYPE.OPEN_TRAVEL
      ? `
fieldset {
    padding-bottom: 0;
    margin-bottom: 0;
  }
`
      : undefined};
  margin-top: 20px;
`;

const WeekdayPickerContainer = styled.div`
  margin-top: 20px;
  fieldset {
    padding-bottom: 0;
    margin-bottom: 10px;
  }
`;

const Message = styled.div`
  margin-bottom: 10px;
`;

const MessageTitle = styled.div`
  font-weight: 600;
`;

const StyledMultiCheckBoxGroup = styled(MultiCheckBoxGroup)`
  & .multicheckbox {
    margin-top: 0;

    .multicheckboxes-container {
      margin-top: 0.5em;
      display: inline-flex;
    }
  }
`;

function OpenTravel({ field, formValues, onChange, updateFormValue, intl, disabled }) {
  const { restrictions } = formValues;

  return (
    <Container>
      {restrictionTypes.map(restriction => {
        const restrictionValues =
          (restrictions && restrictions[RESTRICTION_TYPE.OPEN_TRAVEL]) || [];

        return (
          <div key={restriction}>
            {restrictionValues.map((restrictionValue, key) => {
              return (
                <DatePickerContainer key={restriction + key}>
                  <div>
                    {restrictionValues.length > 1 && (
                      <DeleteTravelDateLink
                        onClick={() =>
                          updateFormValue({
                            action: UPDATE_ENTITY_ACTIONS.DELETE,
                            formValueProperties: `restrictions.${restriction}.${key}`,
                          })
                        }>
                        <FormattedMessage {...messages.deleteTravelDate} />
                      </DeleteTravelDateLink>
                    )}
                  </div>
                  <label>{restrictionTranslations[restriction].START}</label>
                  <DatePickerGroup
                    disabled={disabled}
                    value={restrictionValue && restrictionValue.startDate}
                    field={{
                      ...field,
                      fieldName: `restrictions.${restriction}.${key}.startDate`,
                      displayName: restrictionTranslations[restriction].START,
                      selectsStart: true,
                    }}
                    formValues={{
                      startDate: restrictionValue && restrictionValue.startDate,
                      endDate: restrictionValue && restrictionValue.endDate,
                    }}
                    onChange={onChange}
                  />

                  <DatePickerGroupStyled restrictionType={restriction}>
                    <label>{restrictionTranslations[restriction].END}</label>
                    <DatePickerGroup
                      disabled={disabled}
                      value={restrictionValue && restrictionValue.endDate}
                      field={{
                        ...field,
                        fieldName: `restrictions.${restriction}.${key}.endDate`,
                        displayName: restrictionTranslations[restriction].END,
                        selectsEnd: true,
                      }}
                      formValues={{
                        startDate: restrictionValue && restrictionValue.startDate,
                        endDate: restrictionValue && restrictionValue.endDate,
                      }}
                      onChange={onChange}
                    />
                    <FieldErrorList
                      errors={
                        restrictionValue &&
                        restrictionValue.endDate &&
                        (moment(restrictionValue.endDate).isBefore(restrictionValue.startDate) ||
                          moment(restrictionValue.endDate).isBefore(
                            moment(new Date()).format("YYYY-MM-DD")
                          )) && [intl.formatMessage(messages.endDateAfterStart)]
                      }
                    />
                  </DatePickerGroupStyled>

                  <WeekdayPickerContainer>
                    <Message>
                      <MessageTitle>
                        <FormattedMessage {...messages.weekDays} />
                      </MessageTitle>
                      <FormattedMessage {...messages.weekDaysDescription} />
                    </Message>

                    <StyledMultiCheckBoxGroup
                      value={restrictionValue && restrictionValue.weekDays}
                      field={{
                        disabled: disabled,
                        fieldName: `restrictions.${restriction}.${key}.weekDays`,
                        options: [
                          {
                            ticker: WEEK_DAYS.MONDAY,
                            name: <FormattedMessage {...messages.monday} />,
                          },
                          {
                            ticker: WEEK_DAYS.TUESDAY,
                            name: <FormattedMessage {...messages.tuesday} />,
                          },
                          {
                            ticker: WEEK_DAYS.WEDNESDAY,
                            name: <FormattedMessage {...messages.wednesday} />,
                          },
                          {
                            ticker: WEEK_DAYS.THURSDAY,
                            name: <FormattedMessage {...messages.thursday} />,
                          },
                          {
                            ticker: WEEK_DAYS.FRIDAY,
                            name: <FormattedMessage {...messages.friday} />,
                          },
                          {
                            ticker: WEEK_DAYS.SATURDAY,
                            name: <FormattedMessage {...messages.saturday} />,
                          },
                          {
                            ticker: WEEK_DAYS.SUNDAY,
                            name: <FormattedMessage {...messages.sunday} />,
                          },
                        ],
                      }}
                      onChange={onChange}
                    />
                  </WeekdayPickerContainer>
                </DatePickerContainer>
              );
            })}
          </div>
        );
      })}
    </Container>
  );
}

export default injectIntl(OpenTravel);
